import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { safeOnSnapshot } from '../../utils/firestoreSubscriptions';
import { db } from '../../firebase-client';
import { useAuth } from '../../auth/AuthContext';
import { useOnlineStatus } from '../../utils/onlineStatus';
import {
  LEGAL_UPDATE_SUMMARY,
  LEGAL_UPDATE_VERSION,
  PRIVACY_UPDATED_AT,
  TERMS_UPDATED_AT,
} from '../../utils/legalUpdates';

const LEGAL_ACK_CACHE_PREFIX = 'memberNest.legalAcknowledgement';
const CURRENT_LEGAL_UPDATE_TIME = Math.max(
  Date.parse(`${TERMS_UPDATED_AT}T00:00:00.000Z`),
  Date.parse(`${PRIVACY_UPDATED_AT}T00:00:00.000Z`)
);

export function getLegalAcknowledgementCacheKey(userId, version = LEGAL_UPDATE_VERSION) {
  return `${LEGAL_ACK_CACHE_PREFIX}:${encodeURIComponent(userId || '')}:${encodeURIComponent(version)}`;
}

export function hasCachedLegalAcknowledgement(userId, version = LEGAL_UPDATE_VERSION) {
  if (!userId || typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(getLegalAcknowledgementCacheKey(userId, version)) === '1';
  } catch (err) {
    return false;
  }
}

export function cacheLegalAcknowledgement(userId, version = LEGAL_UPDATE_VERSION) {
  if (!userId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(getLegalAcknowledgementCacheKey(userId, version), '1');
  } catch (err) {}
}

function userCreatedAfterCurrentLegalUpdate(creationTime) {
  const createdAt = Date.parse(creationTime || '');
  return (
    Number.isFinite(createdAt)
    && Number.isFinite(CURRENT_LEGAL_UPDATE_TIME)
    && createdAt >= CURRENT_LEGAL_UPDATE_TIME
  );
}

export default function LegalUpdateAcknowledgement() {
  const { user, authLoading } = useAuth();
  const online = useOnlineStatus();
  const userId = user?.uid || '';
  const userCreationTime = user?.metadata?.creationTime || '';
  const [checking, setChecking] = useState(true);
  const [needsAcknowledgement, setNeedsAcknowledgement] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (authLoading) return undefined;
    if (!userId) {
      setChecking(false);
      setNeedsAcknowledgement(false);
      setAcknowledged(false);
      return undefined;
    }

    if (userCreatedAfterCurrentLegalUpdate(userCreationTime)) {
      setChecking(false);
      setNeedsAcknowledgement(false);
      setAcknowledged(false);
      setError('');
      return undefined;
    }

    setChecking(true);
    setError('');
    setAcknowledged(false);

    const unsubscribe = safeOnSnapshot(
      doc(db, 'users', userId),
      (userSnap) => {
        const data = userSnap.exists() ? userSnap.data() || {} : {};
        const acknowledgedAt = data?.legalAcknowledgements?.[LEGAL_UPDATE_VERSION]?.acknowledgedAt;
        if (acknowledgedAt) {
          cacheLegalAcknowledgement(userId);
          setNeedsAcknowledgement(false);
          setError('');
          setChecking(false);
          return;
        }

        const checkingOfflineCache = !online || userSnap.metadata?.fromCache === true;
        setNeedsAcknowledgement(!checkingOfflineCache);
        setChecking(false);
      },
      () => {
        const hasLocalAcknowledgement = hasCachedLegalAcknowledgement(userId);
        setNeedsAcknowledgement(online && !hasLocalAcknowledgement);
        setError(online && !hasLocalAcknowledgement
          ? 'We could not check your acknowledgement status. Please try again.'
          : '');
        setChecking(false);
      }
    );

    return unsubscribe;
  }, [authLoading, online, userCreationTime, userId]);

  const handleAcknowledge = async () => {
    if (!userId || !acknowledged || saving) return;
    setSaving(true);
    setError('');

    try {
      await setDoc(doc(db, 'users', userId), {
        legalAcknowledgements: {
          [LEGAL_UPDATE_VERSION]: {
            acknowledgedAt: serverTimestamp(),
            termsUpdatedAt: TERMS_UPDATED_AT,
            privacyUpdatedAt: PRIVACY_UPDATED_AT,
            summary: LEGAL_UPDATE_SUMMARY,
          },
        },
      }, { merge: true });
      cacheLegalAcknowledgement(userId);
      setNeedsAcknowledgement(false);
    } catch (err) {
      setError(err?.message || 'Could not save your acknowledgement. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (checking || !needsAcknowledgement) return null;

  return (
    <div className="modal-backdrop legal-update-backdrop">
      <div
        className="modal-content legal-update-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="legal-update-title"
      >
        <h2 id="legal-update-title" className="modal-title">Terms and privacy update</h2>
        <p>
          We have updated our Terms of Service and Privacy Policy to explain uploaded images and
          videos, moderation checks, access controls, retention, and deletion.
        </p>
        <ul className="legal-update-list">
          <li>Uploaded media may be quarantined, scanned, optimized, thumbnailed, and access-controlled before use.</li>
          <li>Flagged uploads may be blocked, reviewed by Member Nest support, approved as false positives, or deleted.</li>
          <li>Unused, incomplete, discarded, moderated, or no-longer-visible uploaded media may be deleted.</li>
          <li>We have clarified OpenAI moderation checks, signed media access, retained evidence, and audit records.</li>
        </ul>
        <p>
          Please read the updated{' '}
          <Link to="/terms-of-service" target="_blank" rel="noopener noreferrer" className="link">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" className="link">
            Privacy Policy
          </Link>
          .
        </p>
        <label className="legal-update-check">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(event) => setAcknowledged(event.target.checked)}
          />
          <span>I acknowledge the updated Terms of Service and Privacy Policy.</span>
        </label>
        {error && <p className="modal-error">{error}</p>}
        <div className="form-actions">
          <button
            type="button"
            className="button button-primary"
            disabled={!acknowledged || saving}
            onClick={handleAcknowledge}
          >
            {saving ? 'Saving...' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
